import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot } from './shared';

/**
 * The Blunderbuss's shots (the Pirate set's gun, ADR-0160 amended): an iron cannonball trailing a wisp of powder smoke, a
 * blast of fire out of the bell and a cloud of powder smoke at the muzzle, a splash of splinters and smoke where it lands;
 * and the flourish's bits: the parrot flapping off (`pirate_parrot`), a gold coin spinning (`pirate_coin`) and the
 * tricorn sent spinning (`pirate_tricorn`).
 */

/** The cannonball, its head at (23, 8): a round of black iron lit at its top-left, a wisp of grey smoke trailing it. */
const ball = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const d = Math.hypot(x - 22, y - 8);
    if (d < 3.2) {
      if (Math.hypot(x - 21, y - 7) < 0.8) return 'w';
      if (d > 2.4) return x + y < 30 ? '3' : 'k';
      return x + y < 29 ? '3' : x + y < 31 ? '2' : '1';
    }
    // The smoke: puffs along its path, the nearer bigger, rolling as it flies.
    for (let i = 0; i < 4; i++) {
      const px = 15 - i * 4.2;
      const py = 8 + Math.sin(i * 1.7 + f * 1.57) * 1.2;
      const r = 2.4 - i * 0.45;
      const e = Math.hypot(x - px, y - py);
      if (e < r) return e < r * 0.5 && i < 2 ? '5' : i < 2 ? '4' : '3';
    }
    return null;
  });

/** A blast of fire out of the bell, then a cloud of powder smoke rolling forward and thinning. */
const blast = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 4, y - 20];
    if (f === 0) {
      // A cone of fire spreading forward.
      const spread = 3 + dx * 0.45;
      if (dx < 0 || dx > 22 || Math.abs(dy) > spread) return null;
      const t = Math.abs(dy) / spread + dx / 30;
      return t < 0.45 ? 'w' : t < 0.75 ? 'y' : t < 1 ? 'o' : null;
    }
    // Lumps of smoke rolling out, fire left in their heart on the first.
    for (const [cx, cy, r] of [
      [10 + f * 3, 20, 6 + f],
      [18 + f * 4, 16 - f, 5 + f],
      [19 + f * 4, 24 + f, 4.5 + f],
      [27 + f * 5, 20, 4 + f],
    ] as const) {
      const d = Math.hypot(x - cx, y - cy);
      if (d >= r) continue;
      if (f === 1 && d < r * 0.4 && cx < 20) return 'o';
      if (f === 2 && d > r * 0.8) continue;
      return y - cy < -r * 0.4 ? '5' : f === 1 ? '4' : y - cy > r * 0.4 ? '3' : '4';
    }
    return null;
  });

/** Splinters flung out of a splash of smoke: a flash, the wood flying, the smoke growing grey and thin. */
const splinters = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    const d = Math.hypot(dx, dy);
    if (f === 0) {
      if (d < 3) return 'w';
      if (d < 5.5) return 'y';
      if (d < 7) return 'o';
    }
    // The splinters: short sticks of wood along their rays, flying out and dropping.
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + 0.4;
      const r0 = 5 + f * 4.5;
      const [ux, uy] = [Math.cos(a), Math.sin(a)];
      const t = dx * ux + (dy - f * f * 0.6) * uy;
      const n = Math.abs(-dx * uy + (dy - f * f * 0.6) * ux);
      if (t > r0 && t < r0 + 3.5 && n < 0.8) return (i % 3 === 0 ? 't' : i % 3 === 1 ? 'm' : 'n') as PaletteKey;
    }
    // The smoke: lumpy, grey, thinning.
    if (f > 0) {
      const R = ([0, 7, 9, 10] as const)[f] as number;
      const lumps = R * (0.85 + 0.15 * Math.sin(Math.atan2(dy, dx) * 5 + f));
      if (d < lumps) {
        if (f === 3 && d > lumps * 0.7) return null;
        if (f === 1 && d < 2.5) return 'y';
        return dx + dy < -R * 0.4 ? '5' : f === 3 ? '3' : '4';
      }
    }
    return null;
  });

/** The parrot flying off (24 × 20, its middle at (12, 10)), facing right: wings up, level, down, level. */
const flying = (f: number) => (c: PixelCanvas) => {
  const lift = ([-1, 0, 1, 0] as const)[f] as number;
  paint(c, (x, y) => {
    // The near wing, beating: its tip up, level, down.
    const [sx, sy] = [11, 10];
    const tip = (
      [
        [3, 1],
        [1, 9],
        [4, 17],
        [1, 9],
      ] as const
    )[f] as readonly [number, number];
    const [vx, vy] = [tip[0] - sx, tip[1] - sy];
    const len = Math.hypot(vx, vy);
    const t = ((x - sx) * vx + (y - sy) * vy) / (len * len);
    const n = Math.abs((x - sx) * vy - (y - sy) * vx) / len;
    if (t > 0 && t < 1 && n < 2.4 * (1 - t * 0.6)) return t < 0.3 ? 'y' : t < 0.75 ? (n < 1 ? 'l' : 'g') : 'G';
    // The head and its beak, the white face, the eye.
    if (x > 19 && x < 22 && y > 8 + lift && y < 11.5 + lift) return y > 10 + lift ? '2' : 'e';
    const head = Math.hypot(x - 17, y - 9 - lift);
    if (head < 2.6) {
      if (Math.hypot(x - 18, y - 8.6 - lift) < 0.7) return 'k';
      if (Math.hypot(x - 18, y - 8.8 - lift) < 1.4) return 'w';
      return 'r';
    }
    // The body, the tail streaming back.
    const body = Math.hypot((x - 12.5) / 5, (y - 11 - lift * 0.5) / 2.6);
    if (body < 1) return y < 10.5 + lift * 0.5 ? 'i' : y > 12 ? 'R' : 'r';
    if (x > 1.5 && x < 8.5 && Math.abs(y - (12 + (8 - x) * 0.45)) < 1.2 - (8 - x) * 0.08) return x < 4 ? 'R' : 'r';
    return null;
  });
  // An outline round it.
  const drawn = (x: number, y: number) => c.get(x, y) !== null && c.get(x, y) !== 'k';
  const rim: [number, number][] = [];
  for (let y = 0; y < c.h; y++)
    for (let x = 0; x < c.w; x++)
      if (c.get(x, y) === null && (drawn(x - 1, y) || drawn(x + 1, y) || drawn(x, y - 1) || drawn(x, y + 1)))
        rim.push([x, y]);
  for (const [x, y] of rim) c.set(x, y, 'k');
};

/** A gold coin spinning: face on, turning, edge on, turning back. */
const coin = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const rx = ([3.5, 2.2, 0.8, 2.2] as const)[f] as number;
    const e = Math.hypot((x - 4) / rx, (y - 4) / 3.5);
    if (e >= 1) return null;
    if (f === 2) return 'y';
    if (e > 0.7) return x < 4 ? 'y' : 'U';
    return f === 0 && Math.abs(x - 4) < 0.6 && Math.abs(y - 4) < 1.6 ? 'U' : 'Y';
  });

/** The tricorn alone (28 × 20, its middle at (14, 10)): black felt, its brim gold-edged, the skull on its front. */
const hat = (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const u = x - 14;
    const d = Math.abs(u);
    if (d > 13) return null;
    const top = 8 - 5 * (d / 13) ** 1.7;
    const bottom = 15 - 2 * (d / 13) ** 2 - Math.max(0, d - 10) * 1.6;
    if (y >= top && y <= bottom) {
      if (y - top < 1.2) return u < 3 ? 'y' : 'Y';
      if (Math.hypot(u, y - 10.5) < 2) return Math.abs(y - 10.3) < 0.6 && Math.abs(Math.abs(u) - 0.8) < 0.5 ? 'k' : 'w';
      if (y > 12 && y < 13.5 && Math.abs(u) < 1.2) return 'w';
      return bottom - y < 1 ? 'k' : u < -6 ? '2' : '1';
    }
    // The crown over it.
    if (Math.hypot(u / 8, (y - 7) / 5) < 1 && y < top + 1) return u < -2 && y < 5 ? '3' : '2';
    return null;
  });

export default [
  sprite({
    name: 'blunder_shot',
    ...shot,
    notes: `The Blunderbuss's bullet (ADR-0160 amended): an iron cannonball at the pellet's head (black iron lit slate at its
top-left, a white glint), puffs of grey powder smoke trailing it, rolling over 4 frames. Turned along its flight. Rubric
(4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { idle: { fps: 12, frames: frames4(ball) } },
  }),
  sprite({
    name: 'blunder_muzzle',
    ...muzzle,
    notes: `The Blunderbuss's muzzle flash (ADR-0160 amended): a cone of fire out of the bell (white, gold, orange), then a
cloud of powder smoke rolling forward in lumps (fire in its heart), thinning. Points right. Rubric (4×): S4 R4 P5 L4 C4
A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(blast) } },
  }),
  sprite({
    name: 'blunder_burst',
    ...burst,
    notes: `The Blunderbuss's hit (ADR-0160 amended): a white and gold flash, then splinters of wood (tan, brown) flung out
and dropping round a lumpy puff of grey smoke that grows and thins. 4 frames. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { burst: { fps: 16, loop: false, frames: frames4(splinters) } },
  }),
  sprite({
    name: 'pirate_parrot',
    group: 'fx',
    size: [24, 20],
    origin: [12, 10],
    density: 4,
    notes: `The Pirate's parrot flying off at a knock-out (ADR-0160 amended): a red body lit pink, a white face, a pale beak,
its wing (a yellow shoulder, green feathers) beating up, level, down over 4 frames, its red tail streaming. Rubric (4×):
S4 R4 P5 L4 C4 A4 F4.`,
    anims: { fly: { fps: 14, frames: frames4(flying) } },
  }),
  sprite({
    name: 'pirate_coin',
    group: 'fx',
    size: [8, 8],
    origin: [4, 4],
    density: 4,
    notes: `A gold coin spinning (the Pirate's knock-out, ADR-0160 amended): face on with a stamp, turning, edge on, turning
back, over 4 frames. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { idle: { fps: 12, frames: frames4(coin) } },
  }),
  sprite({
    name: 'pirate_tricorn',
    group: 'fx',
    size: [28, 20],
    origin: [14, 10],
    density: 4,
    notes: `The Pirate's tricorn knocked off and spinning away (ADR-0160 amended): black felt, its brim's top edged in gold,
a white skull and crossbones on its front; turned by the particle's spin. Rubric (4×): S4 R4 P5 L4 C4 A- F4.`,
    anims: { idle: { fps: 1, frames: [hat] } },
  }),
];
